import { indefiniteArticle } from '../../../../../utils/strings';
import { ListingStatus } from '../listingStatus';
import { getCompetitorTruth } from '../live/waiting/competitorTruth';

export type FaqGroupId = 'getting_started' | 'your_payoff' | 'offers' | 'after_you_accept';

export type FaqLienholder = { slug?: string | null; name?: string | null };

type FaqQuestion = {
  id: string;
  question: string;
  answer: string | ((lienholder: FaqLienholder) => string);
};

type FaqGroup = { label: string; questions: FaqQuestion[] };

const COMPETE_CLOSER =
  'Here, several buyers compete for your car and the offers show up in one place. We also pull your official payoff and handle the lease side with';

const carvanaCarmaxAnswer = ({ slug, name }: FaqLienholder): string => {
  const truth = getCompetitorTruth(slug, name);
  if (truth == null || !name) {
    return `You can, and those sites give you one number from one buyer. ${COMPETE_CLOSER} your lender, which is the part that trips people up when they sell a leased car on their own.`;
  }
  const lease = `${indefiniteArticle(name)} ${name} lease`;
  if (truth.bucket === 'bothCannot') {
    return `Not with ${lease}. Carvana only buys out leases from a short list of leasing companies, and ${name} is not on it. CarMax lists ${name} as a company it cannot purchase leases through. To sell to either one, you would have to buy the car out yourself, retitle it in your name, and then sell it. Here, buyers purchase your car straight from ${name} and you skip all of that.`;
  }
  const [cannot, may] =
    truth.bucket === 'carvanaCannot' ? ['Carvana', 'CarMax'] : ['CarMax', 'Carvana'];
  return `${cannot} will not buy out ${lease}. ${may} may, and that gives you one number from one buyer. ${COMPETE_CLOSER} ${name}.`;
};

export const FAQ_GROUPS: Record<FaqGroupId, FaqGroup> = {
  getting_started: {
    label: 'Getting started',
    questions: [
      {
        id: 'how_it_works',
        question: 'How does selling my leased car here work?',
        answer:
          'Most people hand a leased car back and never find out what it was worth. We pull your official payoff, then buyers compete for your car with offers on your dashboard. If an offer beats your payoff, the difference is yours. If it falls short, you see the gap before you decide anything. A small gap is often less than the turn-in fees, mileage charges, and wear charges your lender would bill you at the end. The winning buyer pays off your lease and picks up the car. There is no fee to you. Finish your listing, then watch for offers.',
      },
      {
        id: 'who_buys',
        question: 'Who buys my car? Is Lease End the buyer?',
        answer:
          'Verified buyers purchase the car, not Lease End. These are businesses that take in lease returns every day and know how to close one out with your lender. We run the marketplace and stay in the middle the whole way: we pull your payoff, show you every offer, and stay on the sale until the car is picked up. Every buyer signs our terms before they can see a single listing. Until you accept, no buyer sees your name or phone number, only the car. When you accept, the winning buyer gets your contact info to arrange paperwork and pickup.',
      },
      {
        id: 'cost',
        question: 'Does this cost me anything?',
        answer:
          'No. Lease End charges you nothing to list or sell. The buyer pays our fee when a sale goes through. There is no sales tax for you either, because you never buy the car yourself. Your lease was written to end with fees: a turn-in fee, plus charges for miles and wear. If an offer lands under your payoff, you pay the gap to sell, and that gap is often smaller than what turning the car in would cost you. Your dashboard puts the two side by side so you can see which one wins for you.',
      },
      {
        id: 'carvana_carmax',
        question: 'Can I sell to Carvana or CarMax instead?',
        answer: carvanaCarmaxAnswer,
      },
      {
        id: 'third_party_buyout',
        question: 'What is a third-party buyout, and will my lender allow one?',
        answer:
          'A third-party buyout is when someone other than you pays off your lease and takes the car. That is how every sale here works. Some lenders limit who can do it and only allow their authorized dealers to buy out a lease. That is where Lease End comes in. We are partnered with authorized dealers who can complete these buyouts, so your lease can still sell here. You list your car the same way, and we handle the lender side.',
      },
      {
        id: 'lease_transfer',
        question: 'Can someone take over my lease instead?',
        answer:
          'No. Lease End does not do lease transfers or swaps. Here, a verified buyer pays off your lease in full and the lease ends. With a transfer, the lease keeps going and, with many lenders, your name stays on it if the new driver stops paying. Paying it off is a clean break. If you would rather keep the car, we can help you with a buyout instead.',
      },
    ],
  },
  your_payoff: {
    label: 'Your payoff',
    questions: [
      {
        id: 'what_is_payoff',
        question: 'What is my payoff, and why does Lease End need it?',
        answer:
          'Your payoff is the full price to end your lease today: your remaining payments plus the buyout price set when you signed. We pull yours straight from your lender. Some lenders quote a different payoff when a business is the one paying it off, and that is the number a sale runs on. We need it before your listing goes live because every offer is measured against it. That is how we show you what an offer means for you, money to you or a gap to cover, before you decide anything.',
      },
      {
        id: 'upside_down',
        question: 'What if my car is worth less than my payoff?',
        answer:
          'That is called being upside down, and it is common on a lease. It does not mean you are stuck. You have a few ways out. You can sell and pay the gap, which is often less than the turn-in fee, mileage, and wear charges waiting at the end of your lease. You can keep the car and we can help you with a buyout. Or you can finish the lease and turn it in. Every lease is different, so we lay out your options and let you choose.',
      },
      {
        id: 'payoff_not_found',
        question: "What if Lease End can't find my payoff?",
        answer:
          "It happens with some lenders. Most payoffs come back in minutes, but some lenders make us call or wait for a business day, and a few will not give it to us at all. If we get stuck, we let you know and give you a couple of ways to move forward. You can message us the payoff yourself from your latest statement or your lender's website, or upload the payoff letter and let us do the rest. Once we have it, your listing goes live.",
      },
      {
        id: 'payoff_changes',
        question: 'What if my payoff changes after I accept or sign?',
        answer:
          'Your payoff has a good-through date from your lender, usually about ten days. We pull a fresh one when you accept an offer and again right before your paperwork is drawn up, so you sign on a current number. Your offer does not change when the payoff does. Only the difference to you moves: a payment you made brings it down, and a few days of interest can nudge it up. If it moves, we tell you the new number. The buyer pays your lender the actual payoff at closing, and what comes to you or what you owe follows from that.',
      },
    ],
  },
  offers: {
    label: 'Offers',
    questions: [
      {
        id: 'offer_timing',
        question: 'How long until I get an offer?',
        answer:
          'Your listing goes live once we have your payoff and your condition report. Your dashboard tells you which step you are on. When buyers are interested, most offers land in the first day or two. Some cars take longer. Offers expire, so answer them as soon as they come in.',
      },
      {
        id: 'mileage_condition',
        question: 'Does mileage and condition matter?',
        answer:
          'Yes. Buyers price your car on exactly what you report, so an honest condition report and clear photos get you offers you can count on. If the car shows up different from what you listed, the buyer can change the offer at pickup. Miles work differently here than at turn-in. Your lender charges you for every mile over your limit when you return the car. When a buyer purchases it, there is no mileage penalty. Extra miles lower what the car is worth, but nobody bills you for them.',
      },
      {
        id: 'keep_the_car',
        question: 'Can I change my mind and keep the car?',
        answer:
          'Yes. Until you accept an offer, you can delist your car from your dashboard whenever you want. Choose "I want to keep the car / buy out my lease instead" as your reason and we switch you over to a buyout right there, no starting over. Once you accept an offer, the sale is in motion, so call us if something changes. If you delist and want the car back up later, call us and we will relist it.',
      },
    ],
  },
  after_you_accept: {
    label: 'After you accept',
    questions: [
      {
        id: 'after_accept',
        question: 'What happens after I accept an offer?',
        answer:
          "Accepting locks in that offer and closes out the others. Your dashboard walks you through the rest. You upload a few things: your driver's license, your registration, a fresh odometer photo, and any car photos still missing. Then you sign the sale paperwork and select pickup times that work for you. The buyer comes to you. No drop-off, no dealership visit, your car leaves from your driveway. Uploading those right away keeps things moving, since the buyer cannot move forward without them.",
      },
      {
        id: 'who_pays',
        question: 'Who pays my lender, and how do I get my money?',
        answer:
          'The buyer pays your lender directly, and that payment ends your lease. You never make the payoff yourself. If your offer was above your payoff, the difference is yours. The buyer pays it to you after they pick up the car. If your offer was below your payoff, you pay the gap to the buyer before pickup, and that is the last cost of your lease. No turn-in fee, no mileage or wear charges after that.',
      },
      {
        id: 'sale_timeline',
        question: 'How long does it take to sell my car?',
        answer:
          'Most sales can wrap up in less than two weeks from the day your listing goes live. Offers usually land in the first few days. Once you accept, uploading your documents and signing take about a day if you move quickly. Pickup is scheduled after that, and most happen within a week. Answer offers and upload your documents right away and you stay at the fast end.',
      },
      {
        id: 'insurance_plates',
        question: 'When can I cancel my insurance, and what do I do with my plates?',
        answer:
          'Keep your insurance until the car is picked up and your lender confirms the lease is paid off. Your lender requires coverage while the lease is open, and canceling early leaves you exposed if anything happens before then. Once your lender shows the lease closed, cancel your policy and ask for a refund of anything you prepaid. Take your plates off before the buyer arrives, unless your state has the plates stay with the car.',
      },
    ],
  },
};

export const FAQ_GROUP_ORDER: Record<ListingStatus, FaqGroupId[]> = {
  building: ['getting_started', 'your_payoff', 'offers', 'after_you_accept'],
  almostLive: ['your_payoff', 'offers', 'getting_started', 'after_you_accept'],
  active: ['offers', 'your_payoff', 'after_you_accept', 'getting_started'],
  parked: ['your_payoff', 'getting_started', 'offers', 'after_you_accept'],
  accepted: ['after_you_accept', 'your_payoff', 'offers', 'getting_started'],
  sold: ['after_you_accept', 'your_payoff', 'getting_started', 'offers'],
  ended: ['offers', 'your_payoff', 'getting_started', 'after_you_accept'],
};
